import type { MeetingStatusEnum } from 'components/meetings/utils/meeting-helpers'

export type EventMembership = {
  userId?: string
  uuid?: string
  name?: string
  email?: string
  kind?: 'HOST' | 'COHOST' | 'INVITEE' | string
  hasBeforeBuffer?: boolean
  hasAfterBuffer?: boolean
  beforeBufferTime?: number
  afterBufferTime?: number
}

export type MeetingEventType = {
  color?: string
  kind?: 'group' | 'solo' | string
  memberships?: EventMembership[]
}

export type MeetingInvitee = {
  name?: string
  email?: string
  uuid?: string
  id?: string | number
}

export type MeetingHost = {
  email?: string
  name?: string
}

export type MeetingIntelligence = {
  uuid?: string
  status?: string
}

export type Meeting = {
  uuid?: string
  key?: string
  id?: string | number
  name?: string
  date?: string

  startTime?: string
  endTime?: string

  cancelled?: boolean
  past?: boolean
  status?: MeetingStatusEnum
  cancellation?: unknown

  source?: string
  location?: string
  locationKind?: string
  locationType?: string

  eventType?: MeetingEventType
  eventMemberships?: EventMembership[]
  ownerUserUuid?: string

  invitee?: MeetingInvitee
  invitees?: MeetingInvitee[]
  host?: MeetingHost
  eventGuestEmails?: string[]

  filledCount?: number
  inviteesLimit?: number

  isCollective?: boolean
  isRoundRobin?: boolean
  isCollectiveRoundRobin?: boolean

  meetingIntelligence?: MeetingIntelligence
}

/**
 * Synthetic event types used as placeholders in the meeting list
 * when there are no real meetings for a time period.
 */
export type EmptyMeetingEvent = {
  key: string
  type: 'NO_TODAY_MEETINGS' | 'NO_FUTURE_MEETINGS' | 'NO_MEETINGS_THIS_DATE'
}

/**
 * Union type for items in a meeting group's events array.
 * Includes both real meetings and empty-state placeholders.
 */
export type MeetingGroupEvent = Meeting | EmptyMeetingEvent

export const isEmptyMeetingEvent = (
  event: MeetingGroupEvent
): event is EmptyMeetingEvent => 'type' in event
  && (
    event.type === 'NO_TODAY_MEETINGS'
    || event.type === 'NO_FUTURE_MEETINGS'
    || event.type === 'NO_MEETINGS_THIS_DATE'
  )
